import React, { useState, useEffect } from 'react';
import { ClockIcon, SealedEnvelopeIcon, UnlockedEnvelopeIcon, TrashIcon } from './Icons';
import { formatDate } from '../utils/helpers';

const CapsuleCard = ({ capsuleGroup, isSender = false, onClick, isTrashed, onDelete, onRestore, friends = [] }) => {
  const [timeRemaining, setTimeRemaining] = useState('');
  const capsule = capsuleGroup[0];

  useEffect(() => {
    if (capsule.status !== 'locked') { setTimeRemaining(''); return; }
    const updateCountdown = () => {
      const diffMs = capsule.unlockDate - new Date();
      if (diffMs <= 0) { setTimeRemaining('Unlocking...'); if (timer) clearInterval(timer); return; }
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
      setTimeRemaining(`${days > 0 ? `${days}d ` : ''}${hours > 0 ? `${hours}h ` : ''}${minutes}m ${seconds}s`);
    };
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [capsule.unlockDate, capsule.status]);

  let displayText, cardState, icon;
  
  if (isSender) {
    const openedCount = capsuleGroup.filter(c => c.status === 'unlocked').length;
    const recipientCount = capsuleGroup.length;
    
    if (capsule.status === 'locked') {
      cardState = 'sent-locked';
      displayText = timeRemaining;
      icon = <SealedEnvelopeIcon />;
    } else if (openedCount < recipientCount) {
      cardState = 'delivered';
      displayText = `Opened by ${openedCount} of ${recipientCount}`;
      icon = <SealedEnvelopeIcon className="text-[#4E9A91] w-6 h-6" />;
    } else {
      cardState = 'opened-all';
      displayText = recipientCount === 1 ? `Opened by recipient` : `Opened by all ${recipientCount} recipients`;
      icon = <UnlockedEnvelopeIcon />;
    }
  } else {
    if (capsule.status === 'unlocked') {
      cardState = 'opened';
      displayText = `Opened on ${formatDate(capsule.unlockDate)}`;
      icon = <UnlockedEnvelopeIcon />;
    } else if (capsule.status === 'ready') {
      cardState = 'ready';
      displayText = 'Ready to Open';
      icon = <SealedEnvelopeIcon className="text-[#4E9A91] w-6 h-6 animate-pulse" />;
    } else {
      cardState = 'locked';
      displayText = timeRemaining;
      icon = <SealedEnvelopeIcon />;
    }
  }

  const sender = !isSender && friends.find(f => f.id === capsule.senderId);
  const senderName = sender ? `@${sender.username}` : capsule.senderEmail;

  const recipientsText = isSender 
    ? `To: ${(capsule.allRecipients || [capsule.recipientEmail]).join(', ')}` 
    : `From: ${senderName}`;

  const isClickable = !isTrashed && onClick && (cardState === 'ready' || cardState === 'opened' || isSender);
  const cardClasses = `bg-[#1E1E1E]/50 p-5 rounded-lg border border-gray-800 flex items-start space-x-4 transition-all duration-300 relative ${isClickable ? 'cursor-pointer hover:bg-gray-800/80 hover:border-[#D4AF37]' : ''} ${isTrashed ? 'opacity-70' : ''}`;

  const Tooltip = () => {
    if (!isSender || capsule.status === 'locked' || capsuleGroup.length <= 1) return null;
    const opened = capsuleGroup.filter(c => c.status === 'unlocked').map(c => c.recipientEmail);
    const unopened = capsuleGroup.filter(c => c.status !== 'unlocked').map(c => c.recipientEmail);
    return (
      <div className="absolute left-0 bottom-full mb-2 w-max bg-gray-700 text-white text-xs rounded py-2 px-3 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
        {opened.length > 0 && <div><strong>Opened:</strong> {opened.join(', ')}</div>}
        {unopened.length > 0 && <div className="mt-1"><strong>Waiting:</strong> {unopened.join(', ')}</div>}
      </div>
    );
  };
  
  const handleCardClick = (e) => {
    if (e.target.closest('button')) return;
    if (isClickable) onClick();
  }

  return (
    <div className={cardClasses} onClick={handleCardClick}>
      <div className="flex-shrink-0 mt-1">{icon}</div>
      <div className="flex-grow">
        <h3 className="font-bold text-lg text-[#EAEAEA]">{capsule.title}</h3>
        <p className="text-sm text-[#A0A0A0] truncate w-60">{recipientsText}</p>
        {isTrashed ? (
            <div className="mt-2">
                <button onClick={(e) => { e.stopPropagation(); onRestore(); }} className="text-sm bg-gray-600 hover:bg-gray-500 text-white font-bold py-1 px-3 rounded-full transition-colors">
                    Restore
                </button>
            </div>
        ) : (
            <div className="relative group">
               <p className={`text-xs mt-1 font-mono ${cardState === 'ready' ? 'text-[#4E9A91] font-bold' : (cardState === 'sent-locked' || cardState === 'locked') ? 'text-[#D4AF37]' : 'text-[#A0A0A0]'}`}>
                <ClockIcon /> {displayText}
              </p>
              <Tooltip />
            </div>
        )}
      </div>
      {!isTrashed && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 transition-colors">
          <TrashIcon className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default CapsuleCard;
